import { Controller } from "@hotwired/stimulus"
import { toastStack } from "../lib/toasts.mjs"

export default class extends Controller {
  static values = { duration: { type: Number, default: 6000 }, inline: Boolean }

  connect() {
    this.ink = this.element.classList.contains("toast-notification")
    if (!this.ink) {
      if (this.element.getAttribute("data-auto-hide") === "true") {
        this.timer = setTimeout(() => this.close(), 4000)
      }
      return
    }
    // Portaling may reconnect Stimulus once. Retain the original focus destination.
    if (!this.returnFocus) this.returnFocus = document.activeElement
    if (!this.inlineValue && this.element.parentElement !== toastStack()) {
      this.sourceElement = this.element.parentElement === document.body ? null : this.element.parentElement
      toastStack().prepend(this.element)
    }
    this.remaining = this.durationValue
    this.closing = false
    this.pauses = new Set()
    // Legacy block messages can contain a link/form outside the dedicated action slot.
    this.persistent = this.durationValue <= 0 ||
      this.element.classList.contains("toast-notification--error") ||
      Boolean(this.element.querySelector("a, form, .toast-notification__actions"))
    if (document.hidden) this.pauses.add("hidden")
    if (this.element.contains(document.activeElement)) this.pauses.add("focus")
    if (this.element.matches(":hover")) this.pauses.add("hover")
    this.startTimer()
  }

  disconnect() {
    clearTimeout(this.timer)
    clearTimeout(this.removalTimer)
    this.timer = null
  }

  startTimer() {
    if (this.persistent || this.closing || this.pauses.size || this.timer) return
    this.startedAt = Date.now()
    this.timer = setTimeout(() => {
      this.timer = null
      this.close()
    }, Math.max(0, this.remaining))
  }

  addPause(reason) {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
      this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt))
    }
    this.pauses.add(reason)
  }

  pause(event) {
    this.addPause(event.type === "mouseenter" ? "hover" : "focus")
  }

  resume(event) {
    if (event.type === "focusout" && this.element.contains(event.relatedTarget)) return
    this.pauses.delete(event.type === "mouseleave" ? "hover" : "focus")
    this.startTimer()
  }

  visibilityChanged() {
    if (document.hidden) this.addPause("hidden")
    else {
      this.pauses.delete("hidden")
      this.startTimer()
    }
  }

  close() {
    if (this.closing) return
    this.closing = true
    clearTimeout(this.timer)
    this.timer = null
    if (!this.ink) {
      this.element.classList.add("opacity-0")
      this.removalTimer = setTimeout(() => this.element.remove(), 1000)
      return
    }
    // Never move focus for a timed confirmation when the user is working elsewhere.
    if (this.element.contains(document.activeElement)) {
      const destination = this.returnFocus?.isConnected &&
        ![document.body, document.documentElement].includes(this.returnFocus) &&
        !this.element.contains(this.returnFocus)
        ? this.returnFocus : document.getElementById("toast-stack")
      destination?.focus({ preventScroll: true })
    }
    this.element.classList.add("toast-notification--leaving")
    const delay = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 160
    this.removalTimer = setTimeout(() => this.element.remove(), delay)
  }

  replaceSource(event) {
    if (!this.sourceElement) return
    const stream = event.detail.newStream
    if (stream && !["update", "replace", "remove"].includes(stream.action)) return
    const targets = stream ? stream.targetElements : [event.target]
    if (!targets.some(target => target.contains(this.sourceElement))) return
    // Keep the original container's replacement semantics after portaling. Wrap the
    // render so canceled/deferred Turbo updates do not clear the current feedback.
    const render = event.detail.render
    event.detail.render = (...args) => {
      this.discard()
      return render(...args)
    }
  }

  discardDetachedSource() {
    if (this.sourceElement && !this.sourceElement.isConnected) this.discard()
  }

  discard() {
    if (this.inlineValue) return
    this.element.remove()
  }

  reload() {
    window.location.reload()
  }
}
